import { useEffect, useMemo, useState } from 'react';
import { usePickerDate } from '../usePickerDate';
import { CAR_DRIVEN, DAMAGE_DEGREE, OTHER_PARTY } from '@/consts/bodyAndPaint';
import {
  CarConditionEntryItemType,
  DamageMapItemType,
} from '@/types/bodyAndPaint';

interface CarConditionStateHookProps {
  initial: CarConditionEntryItemType;
}
export const useCarConditionState = ({
  initial,
}: CarConditionStateHookProps) => {
  const [carConditionState, setCarConditionState] = useState(
    initial ?? ({} as CarConditionEntryItemType),
  );
  useEffect(() => {
    setCarConditionState((prev) => {
      if (initial) {
        if (JSON.stringify(prev) !== JSON.stringify(initial)) {
          return initial;
        } else {
          return prev;
        }
      } else {
        return prev;
      }
    });
  }, [initial]);

  const carConditionSetState = useMemo(() => {
    return {
      isDrivenSelect: (select: number) => {
        setCarConditionState((prev) => ({ ...prev, isDrivenSelect: select }));
      },
      damageMapCheck: (key: keyof DamageMapItemType) => {
        setCarConditionState((prev) => ({
          ...prev,
          damageMapCheck: {
            ...prev.damageMapCheck,
            [key]: !prev.damageMapCheck?.[key],
          },
        }));
      },
      damageDegreeSelect: (select: number) => {
        setCarConditionState((prev) => ({
          ...prev,
          damageDegreeSelect: select,
        }));
      },
      otherPartSelect: (select: number) => {
        setCarConditionState((prev) => ({ ...prev, otherPartSelect: select }));
      },
      noticeDateText: (text: string) => {
        setCarConditionState((prev) => ({ ...prev, noticeDateText: text }));
      },
      descriptionText: (text: string) => {
        setCarConditionState((prev) => ({ ...prev, descriptionText: text }));
      },
    };
  }, []);

  const carConditionSelectList = useMemo(() => {
    return {
      isDrivenSelect: Object.values(CAR_DRIVEN).map((item) => item.label),
      damageDegreeSelect: Object.values(DAMAGE_DEGREE).map(
        (item) => item.label,
      ),
      otherPartSelect: Object.values(OTHER_PARTY).map((item) => item.label),
    };
  }, []);
  const carConditionError = useMemo(() => {
    const noticeDatePattern = '^\\d{4}年\\d{1,2}月\\d{1,2}日$';
    const descriptionPattern = '';
    return {
      noticeDateText: noticeDatePattern,
      descriptionText: descriptionPattern,
    };
  }, []);

  const picker = usePickerDate({
    initialDate: initial?.noticeDateText
      ? new Date(initial?.noticeDateText)
      : undefined,
  });
  const pickerInfo = useMemo(() => {
    return {
      pickerSelect: picker.pickerSelect,
      pickerInputID: picker.pickerInputID,
      pickerContainerID: picker.pickerContainerID,
    };
  }, [picker]);
  useEffect(() => {
    carConditionSetState.noticeDateText(pickerInfo.pickerSelect);
  }, [carConditionSetState, pickerInfo.pickerSelect]);

  const disabled = useMemo(() => {
    const {
      isDrivenSelect,
      damageMapCheck,
      damageDegreeSelect,
      otherPartSelect,
      noticeDateText,
      descriptionText,
    } = carConditionState;

    const carDrivenCheck = isDrivenSelect !== undefined && isDrivenSelect >= 0;
    const damageMapCheckCheck = Object.values(damageMapCheck ?? {}).some(
      (item) => item,
    );
    const damageDegreeCheck =
      damageDegreeSelect !== undefined && damageDegreeSelect >= 0;
    const otherPartyCheck =
      otherPartSelect !== undefined && otherPartSelect >= 0;
    const noticeDateCheck = !!(
      noticeDateText !== undefined &&
      noticeDateText !== '' &&
      new RegExp(carConditionError.noticeDateText).test(noticeDateText)
    );

    const descriptionCheck =
      descriptionText !== undefined && descriptionText !== '';
    return !(
      carDrivenCheck &&
      damageMapCheckCheck &&
      damageDegreeCheck &&
      otherPartyCheck &&
      noticeDateCheck &&
      descriptionCheck
    );
  }, [carConditionState]);

  return {
    state: carConditionState,
    setState: carConditionSetState,
    list: carConditionSelectList,
    error: carConditionError,
    pickerInfo,
    disabled: disabled,
  };
};
